import React, { useState, useEffect } from "react";
// Components
import Input from "../Input";
import Select from "../Select";
import MultiSelectWithAdd from "../MultiSelectWithAdd";
import { YES_NO_OPTIONS } from "components/userForms/constants";
import { Link } from "lucide-react";

const HealthDetails = ({
  formData,
  formErrors,
  handleChange,
  index,
  inputRefs,
}) => {
  const [drugOptions, setDrugOptions] = useState([]);
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (!formData.medicalIssues) return;
    const issues = formData.medicalIssues
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (issues.length === 0) {
      setDrugOptions([]);
      return;
    }
    let debounceTimer;

    if (issues) {
      debounceTimer = setTimeout(() => {
        const fetchDrugs = async () => {
          const searchQuery = issues
            .map(
              (issue) =>
                `patient.reaction.reactionmeddrapt:"${encodeURIComponent(
                  issue
                )}"`
            )
            .join("+OR+");

          const url = `https://api.fda.gov/drug/event.json?search=${searchQuery}&limit=100`;

          try {
            const res = await fetch(url);
            const data = await res.json();

            const allDrugs =
              (data.results || [])
                .flatMap((r) => r.patient.drug)
                .map((d) => d.medicinalproduct)
                .filter(Boolean) || [];

            const uniqueDrugs = [...new Set(allDrugs)];
            setDrugOptions(uniqueDrugs);
          } catch (err) {
            console.error("Error fetching medications", err);
            setDrugOptions([]);
          } finally {
            setIsEdited(false);
          }
        };
        if (isEdited) fetchDrugs();
      }, 600);
    } else {
      setDrugOptions([]);
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [formData.medicalIssues]);

  const packageOptionText =
    "I am in good health and can manage walking, standing, and carrying my own bags, including up stairs. The tour schedule requires guests to walk at a moderate pace on uneven terrain (an average of 1–3 miles per day)";

  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <MultiSelectWithAdd
          label="Medical Issues"
          value={formData.medicalIssues || ""}
          onChange={handleChange}
          setIsEdited={setIsEdited}
          name={"medicalIssues"}
          placeHolder="Add a medical issue"
          ref={inputRefs.medicalIssues}
          // error={formErrors.medicalIssues}
        />

        <MultiSelectWithAdd
          label="Medications"
          options={drugOptions}
          name={"medications"}
          value={formData.medications || ""}
          onChange={handleChange}
          placeHolder="Search or add drugs..."
          ref={inputRefs.medications}
          // error={formErrors.medications}
        />
        <Select
          label="Allergies"
          name="allergies"
          value={formData.allergies || ""}
          onChange={handleChange}
          options={YES_NO_OPTIONS}
          ref={inputRefs.allergies}
          // error={formErrors.allergies}
        />
        <div>
          <Select
            label="Walking Issue?"
            name="walkIssue"
            value={formData.walkIssue || ""}
            onChange={handleChange}
            options={YES_NO_OPTIONS}
            ref={inputRefs.walkIssue}
            // error={formErrors.walkIssue}
          />

          {/* Conditional render */}
          {formData.walkIssue === "yes" && (
            <div className="text-sm text-yellow-800">
              The tour schedule requires guests to walk at a moderate pace on
              uneven terrain (an average of 1–3 miles per day)
            </div>
          )}
        </div>

        <Select
          label="Car/Bus Sickness?"
          name="carSick"
          value={formData.carSick || ""}
          onChange={handleChange}
          options={YES_NO_OPTIONS}
          ref={inputRefs.carSick}
          // error={formErrors.carSick}
        />

        <Select
          label="Sea Sickness?"
          name="seaSick"
          value={formData.seaSick || ""}
          onChange={handleChange}
          options={YES_NO_OPTIONS}
          ref={inputRefs.seaSick}
          // error={formErrors.seaSick}
        />

        <Select
          label="Food Allergies"
          name="foodAllergies"
          value={formData.foodAllergies || ""}
          onChange={handleChange}
          options={YES_NO_OPTIONS}
          ref={inputRefs.foodAllergies}
          // error={formErrors.foodAllergies}
        />

        <div className="">
          <label className="block text-inputColor text-sm font-normal mb-1">
            Dietary Preferences
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              "Vegetarian",
              "Biblically Clean Meat",
              "Biblically Clean Fish",
              "Vegan",
            ].map((option) => (
              <div key={option} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="dietaryPreferences"
                  value={option}
                  checked={
                    Array.isArray(formData.dietaryPreferences)
                      ? formData.dietaryPreferences.includes(option)
                      : false
                  }
                  onChange={(e) => {
                    const checked = e.target.checked;
                    let updated = Array.isArray(formData.dietaryPreferences)
                      ? [...formData.dietaryPreferences]
                      : [];

                    if (checked) {
                      updated.push(option);
                    } else {
                      updated = updated.filter((item) => item !== option);
                    }
                    // if (checked) {
                    //   // Add the option
                    //   if (!updated.includes(option)) updated.push(option);

                    //   // Auto-select Vegetarian if Clean Meat or Clean Fish chosen
                    //   if (
                    //     (option === "Biblically Clean Meat" ||
                    //       option === "Biblically Clean Fish") &&
                    //     !updated.includes("Vegetarian")
                    //   ) {
                    //     updated.push("Vegetarian");
                    //   }
                    // } else {
                    //   // Remove the option
                    //   updated = updated.filter((item) => item !== option);

                    //   // If Vegetarian is unchecked, also remove Clean Meat / Clean Fish
                    //   if (option === "Vegetarian") {
                    //     updated = updated.filter(
                    //       (item) =>
                    //         item !== "Clean Meat" && item !== "Clean Fish"
                    //     );
                    //   }
                    // }

                    handleChange({
                      target: { name: "dietaryPreferences", value: updated },
                    });
                  }}
                  className="w-3 h-3 text-primary border-gray-300 rounded"
                  ref={inputRefs.dietaryPreferences}
                />
                <div className="flex items-center gap-1">
                  <span className="text-sm font-normal text-activeTabColor">
                    {option}
                  </span>

                  {/* Add the link icon for specific options */}
                  {(option === "Biblically Clean Meat" ||
                    option === "Biblically Clean Fish") && (
                    <div className="relative group">
                      <Link
                        size={14}
                        className="text-blue-600 cursor-pointer hover:text-blue-800"
                        onClick={() =>
                          window.open(
                            "https://www.biblegateway.com/passage/?search=Leviticus%2011&version=NKJV",
                            "_blank"
                          )
                        }
                      />

                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                        {option === "Biblically Clean Meat"
                          ? "Know this better"
                          : "Know this better"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div></div>
        <div className="-mt-3">
          <Input
            label="Food Preferences / Restrictions"
            name="dietaryPreferencesDetails"
            value={formData.dietaryPreferencesDetails}
            onChange={handleChange}
            ref={inputRefs.dietaryPreferencesDetails}
          />
        </div>
      </div>
      <div className="flex items-start mt-2 gap-2">
        <input
          type="checkbox"
          name="packageOption"
          checked={formData.packageOption === packageOptionText}
          onChange={(e) =>
            handleChange({
              target: {
                name: "packageOption",
                value: e.target.checked ? packageOptionText : "",
              },
            })
          }
          className="w-4 h-4 text-primary border-gray-300"
          ref={inputRefs.packageOption}
        />
        <span className="text-sm">{packageOptionText}</span>
      </div>
    </>
  );
};

export default HealthDetails;
